/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useContext, useState } from 'react';
import { User } from '@/types/models';
import { useNavigate } from 'react-router';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { AppRoutes } from '@/constants/routes';
import { useQuery } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { get } from '@/utils/networks.ts';
import { ConfigENV } from '@/init';
import { useLocalStorage } from 'primereact/hooks';

type ExternalServiceConfig = {
  spaces: {
    serviceUp: boolean;
    url: string | undefined;
    topic: string | undefined;
  };
  trigger: {
    serviceUp: boolean;
    url: string | undefined;
  };
};
interface StoreContextProviderProps {
  antflowServerConfig: ConfigENV['SERVER_CONFIG'];
  children?: React.ReactNode | undefined;
  externalServiceConfig?: ExternalServiceConfig;
  antflowProcessApiEndpoint: AntflowApiEndpoint;
}

interface StoreContext {
  currentUser?: User | null;
  antflowProcessApiEndpoint: AntflowApiEndpoint;
  logOut?: () => void;
  children?: React.ReactNode | undefined;
  externalServiceConfig?: ExternalServiceConfig;
  antflowServerConfig: ConfigENV['SERVER_CONFIG'];
}

// eslint-disable-next-line react-refresh/only-export-components
export const StoreContext = React.createContext<StoreContext>({} as StoreContext);

export const StoreContextProvider: React.FC<StoreContextProviderProps> = ({
  antflowServerConfig,
  antflowProcessApiEndpoint,
  children,
  externalServiceConfig
}) => {
  const navigate = useNavigate();

  const [, setOffline] = useLocalStorage(false, 'offline');

  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const [serverConfiguration, setServerConfiguration] = useState<ConfigENV['SERVER_CONFIG']>(antflowServerConfig);

  const logOut = useCallback(() => {
    const redirect_uri = window.location.origin;
    antflowProcessApiEndpoint.logout(redirect_uri).finally(() => {
      setCurrentUser(null);
      setOffline(true);
      localStorage.removeItem('hide_token_alert');
      if (!window.location.hash.startsWith(`#${AppRoutes.docs.path}`)) {
        navigate(AppRoutes.login.path);
      }
    });
  }, [antflowProcessApiEndpoint, navigate, setOffline]);

  const { isPending } = useQuery({
    queryKey: ['isLogged'],
    queryFn: async () => {
      const configPromise = get<Record<string, unknown>>(antflowServerConfig.apiUrl.concat('/openapi.json'))
        .then((configurationResponse) => {
          const configData = configurationResponse.data;

          if (configData instanceof Object && configData.hasOwnProperty('servers')) {
            const { servers } = configData;

            if (servers && Array.isArray(servers)) {
              setServerConfiguration((prev) => ({
                ...prev,
                apiPrefix: servers[0].url || ''
              }));
            }
          }
        })
        .catch((error) => console.error('Failed to fetch server configuration:', error));

      const authPromise = antflowProcessApiEndpoint
        .get_user_info()
        .then((authResponse) => {
          if (authResponse) {
            setCurrentUser(authResponse);
          }
        })
        .catch((error) => console.error('Session e', error));

      return Promise.allSettled([configPromise, authPromise]);
    },
    throwOnError: false,
    retry: false,
    refetchOnWindowFocus: false
  });

  return (
    <StoreContext.Provider
      value={{
        currentUser,
        antflowProcessApiEndpoint,
        antflowServerConfig: serverConfiguration,
        externalServiceConfig,
        logOut
      }}
    >
      {isPending ? (
        <main className="flex flex-column align-items-center justify-content-center w-full h-full items-center">
          <Loader />
        </main>
      ) : (
        children
      )}
    </StoreContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useStoreContext = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStoreContext must be used within an StoreContextProvider');
  }
  return context;
};
